var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import Select from '@material-ui/core/Select';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ListSubheader from '@material-ui/core/ListSubheader';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import clsx from 'clsx';
import { useForm, useFormRefs, useFormRules, useGetRuleName, useBaseI18n, useTextFieldError, useDirection } from '../hooks';
import { dirStyleGenerator } from '../functions';
import { useFormInputStyles } from './FormInput';
import FormHelperText from './FormHelperText';
var FormSelect = function (_a) {
    var id = _a.id, dir = _a.dir, description = _a.description, o = _a.options, g = _a.groups, _b = _a.classes, _c = _b === void 0 ? {} : _b, container = _c.container, labelContainer = _c.labelContainer, label = _c.label, errorContainer = _c.errorContainer, root = _c.root, inputRoot = _c.inputRoot, select = _c.select, icon = _c.icon, inputClasses = __rest(_c, ["container", "labelContainer", "label", "errorContainer", "root", "inputRoot", "select", "icon"]), rest = __rest(_a, ["id", "dir", "description", "options", "groups", "classes"]);
    var _d = useState(null), containerTarget = _d[0], setContainerTarget = _d[1];
    var formInputClasses = useFormInputStyles();
    var fieldError = useTextFieldError(id);
    var getRuleName = useGetRuleName();
    var direction = useDirection();
    var formRules = useFormRules();
    var innerRef = useFormRefs();
    var form = useForm();
    var c = useBaseI18n(function (i18n) { return i18n.form.choose; });
    var ruleName = useMemo(function () {
        return getRuleName(formRules[id]);
    }, [formRules, getRuleName, id]);
    var options = useMemo(function () {
        var _a;
        return o !== null && o !== void 0 ? o : (_a = formRules[id]) === null || _a === void 0 ? void 0 : _a.options;
    }, [formRules, o, id]);
    var groups = useMemo(function () {
        var _a;
        return g !== null && g !== void 0 ? g : (_a = formRules[id]) === null || _a === void 0 ? void 0 : _a.groups;
    }, [formRules, g, id]);
    var groupOptions = useMemo(function () {
        if (!groups)
            return;
        var groupOptions = [];
        for (var _i = 0, groups_1 = groups; _i < groups_1.length; _i++) {
            var group = groups_1[_i];
            groupOptions.push({ type: 'header', value: group.header });
            groupOptions.push.apply(groupOptions, group.options);
        }
        return groupOptions;
    }, [groups]);
    var classes = useStyles({ dir: dir || direction });
    var option = useMemo(function () { return form.values[id]; }, [form.values[id]]);
    var onChange = function (event) {
        var newOption = (options !== null && options !== void 0 ? options : groupOptions).find(function (option) { return option.id == event.target.value; });
        form.setFieldValue(id, newOption);
    };
    return (_jsxs("div", __assign({ ref: setContainerTarget, className: container }, { children: [_jsx("div", __assign({ className: clsx(formInputClasses.containLables, labelContainer) }, { children: _jsx(InputLabel, __assign({ htmlFor: id, className: clsx(formInputClasses.label, label) }, { children: description }), void 0) }), void 0),
            _jsxs(Select, __assign({ id: id, disableUnderline: true, dir: dir || direction, className: clsx(classes.inputRoot, inputRoot), classes: __assign({ root: clsx(formInputClasses.input, fieldError && formInputClasses.border, classes.root, root), select: clsx(classes.select, select), icon: classes.icon }, inputClasses), inputRef: function (el) { if (innerRef)
                    innerRef.current[id] = el; }, value: (option === null || option === void 0 ? void 0 : option.id) || -1, onChange: onChange, MenuProps: {
                    container: containerTarget,
                    classes: {
                        list: classes.list
                    }
                } }, rest, { children: [_jsxs(MenuItem, __assign({ disabled: true, value: -1, className: classes.menuItem }, { children: [c, " ", ruleName] }), void 0),
                    options === null || options === void 0 ? void 0 : options.map(function (option, index) { return (_jsx(MenuItem, __assign({ value: option.id, className: classes.menuItem }, { children: option.value }), index)); }),
                    groupOptions === null || groupOptions === void 0 ? void 0 : groupOptions.map(function (option, index) { return (option.type === 'header' ? (_jsx(ListSubheader, { children: option.value }, index)) : (_jsx(MenuItem, __assign({ value: option.id, className: classes.menuItem }, { children: option.value }), index))); })] }), void 0),
            _jsx(FormHelperText, { className: errorContainer, text: fieldError, error: true }, void 0)] }), void 0));
};
export default FormSelect;
var useStyles = makeStyles(function (theme) {
    var _a, _b, _c, _d;
    return ({
        inputRoot: {
            width: '100%'
        },
        root: __assign({}, dirStyleGenerator('padding', '24px !important')),
        select: (_a = {
                alignItems: 'center'
            },
            _a[theme.breakpoints.up(500)] = {
                padding: '4px 7px',
                fontSize: 17
            },
            _a[theme.breakpoints.down(500)] = {
                padding: '6px 7px',
                fontSize: 22
            },
            _a[theme.breakpoints.up(1600)] = {
                padding: '9px 10px',
                fontSize: 23
            },
            _a['&:focus'] = {
                borderRadius: '5px !important'
            },
            _a),
        icon: (_b = {
                position: 'absolute'
            },
            _b[theme.breakpoints.up(500)] = __assign(__assign({}, dirStyleGenerator('', 4)), { width: 30, height: 30 }),
            _b[theme.breakpoints.down(500)] = __assign(__assign({}, dirStyleGenerator('', 4)), { width: 30, height: 30 }),
            _b[theme.breakpoints.up(1600)] = __assign(__assign({}, dirStyleGenerator('', 10)), { width: 40, height: 40 }),
            _b.color = theme.palette.primary.main,
            _b),
        list: (_c = {},
            _c[theme.breakpoints.up(500)] = {
                padding: '4px 0'
            },
            _c[theme.breakpoints.down(500)] = {
                padding: '6px 0'
            },
            _c[theme.breakpoints.up(1600)] = {
                padding: '9px 0'
            },
            _c),
        menuItem: (_d = {
                fontFamily: 'Assistant-Regular',
                color: theme.palette.primary.main
            },
            _d[theme.breakpoints.up(500)] = {
                padding: '4px 15px',
                fontSize: 17
            },
            _d[theme.breakpoints.down(500)] = {
                padding: '6px 15px',
                fontSize: 22
            },
            _d[theme.breakpoints.up(1600)] = {
                padding: '9px 15px',
                fontSize: 23
            },
            _d)
    });
});
//# sourceMappingURL=FormSelect.js.map